/* eslint-disable react/forbid-component-props */
import { useRef } from 'react';
import { useUniqueIdentifier } from '@rainbow-modules/hooks';
import { InternalTooltip, useDefaultTooltipConnector } from 'react-rainbow-components';
import CalendarBuiltInStatusBadge from 'components/CalendarBuiltInStatusBadge';
import getTimeLabel from 'components/CalendarListView/helpers/getTimeLabel';
import { EventStatus } from 'data/firestore/globals';
import { CalendarEventClick } from 'types';
import getHeightOfMinutes from '../helpers/getHeightOfMinutes';
import useEventStyle from './hooks/useEventStyle';
import {
    StyledEvent,
    StyledEventBackground,
    StyledEventContent,
    StyledEventTitle,
    StyledEventDescription,
    EventTooltipContent,
    EventTooltipHeader,
    EventTooltipTitle,
    EventTooltipBadgeSlot,
    EventTooltipLine,
    EventTooltipLabel,
    EventSize,
} from './styled';

const COMPACT_EVENT_MAX_HEIGHT = 24;
const TINY_EVENT_MAX_HEIGHT = 12;

const getEventSize = (durationInMinutes: number): EventSize => {
    const height = getHeightOfMinutes(durationInMinutes) - 1;
    if (height < TINY_EVENT_MAX_HEIGHT) return 'tiny';
    if (height < COMPACT_EVENT_MAX_HEIGHT) return 'compact';
    return 'default';
};

interface Params {
    id?: string;
    title: string;
    description: string;
    startAt: Date;
    duration: number;
    color: string;
    status?: EventStatus;
    calendarName?: string;
    onEventClick?: CalendarEventClick;
    timeZone: string;
    isSelected?: boolean;
    isPending?: boolean;
    isRejected?: boolean;
    positionInSlot?: {
        left: number;
        width?: number;
    };
}

const Event = ({
    onEventClick = () => {},
    timeZone,
    isSelected = false,
    isPending = false,
    isRejected = false,
    positionInSlot = {
        left: 0,
    },
    status,
    calendarName,
    ...event
}: Params) => {
    const {
        title, description, color, startAt, duration,
    } = event;
    const eventId = useUniqueIdentifier('calendar-event');
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef();
    const {
        onMouseEnter,
        onMouseLeave,
        isVisible,
    } = useDefaultTooltipConnector({
        tooltipRef,
        triggerRef: () => triggerRef as any,
    });
    const style = useEventStyle({
        startDate: startAt,
        duration: duration / 60,
        timeZone,
    });
    const timeLabel = getTimeLabel({
        startAt,
        duration,
        timezone: timeZone,
    });

    return (
        <>
            <StyledEvent
                id={`day-view-event__${eventId}`}
                ref={triggerRef}
                left={positionInSlot.left}
                width={positionInSlot.width}
                style={style}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={(e) => {
                    e.stopPropagation();
                    onMouseLeave();
                    return onEventClick({
                        triggerRef: {
                            current: e.target,
                        },
                        event,
                    });
                }}
            >
                <StyledEventBackground
                    color={color}
                    statusColor={status?.color}
                    size={getEventSize(duration / 60)}
                    isSelected={isSelected}
                    isPending={isPending}
                    isRejected={isRejected}
                >
                    <StyledEventContent>
                        <StyledEventTitle>{title}</StyledEventTitle>
                        <StyledEventDescription>{description}</StyledEventDescription>
                    </StyledEventContent>
                </StyledEventBackground>
            </StyledEvent>
            <InternalTooltip
                triggerElementRef={() => triggerRef}
                isVisible={isVisible}
                preferredPosition="top"
                ref={tooltipRef}
            >
                <EventTooltipContent>
                    <EventTooltipHeader>
                        <EventTooltipTitle>{title}</EventTooltipTitle>
                        {status ? (
                            <EventTooltipBadgeSlot>
                                <CalendarBuiltInStatusBadge value={status} variant="solid" />
                            </EventTooltipBadgeSlot>
                        ) : null}
                    </EventTooltipHeader>
                    {description ? (
                        <EventTooltipLine>
                            <EventTooltipLabel>Event:</EventTooltipLabel>
                            {description}
                        </EventTooltipLine>
                    ) : null}
                    {calendarName ? (
                        <EventTooltipLine>
                            <EventTooltipLabel>Calendar:</EventTooltipLabel>
                            {calendarName}
                        </EventTooltipLine>
                    ) : null}
                    <EventTooltipLine>
                        <EventTooltipLabel>Time:</EventTooltipLabel>
                        {timeLabel}
                    </EventTooltipLine>
                </EventTooltipContent>
            </InternalTooltip>
        </>
    );
};

export default Event;
